import { Controller } from '@hotwired/stimulus'
import { escapeHtml, knownLabels, questionLabels } from '../helpers'

const PILL = 'inline-flex items-center align-baseline rounded px-1.5 py-0.5 text-sm font-semibold mx-0.5 select-none'
const PREVIEW_PILL = 'inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-bold align-middle'
const TONE = 'bg-blue-100 text-blue-700'
const TONE_MISSING = 'bg-red-50 text-red-700'
const MISSING_HINT = 'This question no longer exists, so it will be left out of the title.'
const CHIP_BASE = 'inline-flex items-center gap-1 px-3 py-1 text-xs font-medium rounded-full border transition'
const CHIP_IDLE = 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100'
const CHIP_FILLED = 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700'

export default class extends Controller {
  static targets = ['editor', 'field', 'chips', 'preview']

  connect () {
    this.known = knownLabels()
    this.renderFromValue(this.fieldTarget.value)
    this.refreshChips()
    this.renderPreview()
  }

  onInput () { this.commit() }

  onKeydown (event) {
    if (event.key === 'Enter') event.preventDefault()
  }

  onPaste (event) {
    event.preventDefault()
    const text = (event.clipboardData || window.clipboardData).getData('text').replace(/\s+/g, ' ')
    this.insertText(text)
    this.commit()
  }

  clear (event) {
    if (event) event.preventDefault()
    this.editorTarget.innerHTML = ''
    this.commit()
    this.editorTarget.focus()
  }

  insert (event) {
    event.preventDefault()
    const label = event.currentTarget.dataset.label
    if (!label) return
    this.editorTarget.focus()
    if (this.serialize().includes(`{{${label}}}`)) {
      this.editorTarget.querySelectorAll('[data-label]').forEach((el) => { if (el.dataset.label === label) el.remove() })
    } else {
      this.insertNode(this.pill(label))
    }
    this.commit()
  }

  refreshChips () {
    if (!this.hasChipsTarget) return
    const labels = questionLabels()

    if (!labels.length) {
      this.chipsTarget.innerHTML = '<span class="text-xs text-gray-400 italic">Add questions first — then drop their answers in here.</span>'
      return
    }
    this.chipsTarget.innerHTML = labels.map((label) => {
      const safe = escapeHtml(label)
      return `<button type="button" data-action="token-field#insert" data-label="${safe}" class="${CHIP_BASE} ${CHIP_IDLE}"><span data-mark>+</span> ${safe}</button>`
    }).join('')
    this.updateChipStates()
  }

  updateChipStates () {
    if (!this.hasChipsTarget) return
    const value = this.serialize()
    this.chipsTarget.querySelectorAll('button[data-label]').forEach((btn) => {
      const used = value.includes(`{{${btn.dataset.label}}}`)
      btn.className = `${CHIP_BASE} ${used ? CHIP_FILLED : CHIP_IDLE}`
      const mark = btn.querySelector('[data-mark]')
      if (mark) mark.textContent = used ? '✓' : '+'
    })
  }

  commit () {
    this.fieldTarget.value = this.serialize()
    this.fieldTarget.dispatchEvent(new Event('input', { bubbles: true }))
    this.updateChipStates()
    this.renderPreview()
  }

  serialize () {
    let out = ''
    this.editorTarget.childNodes.forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE) out += node.textContent
      else if (node.dataset && node.dataset.label) out += `{{${node.dataset.label}}}`
      else if (node.nodeName !== 'BR') out += node.textContent || ''
    })
    return out.trim()
  }

  renderFromValue (value) {
    this.editorTarget.innerHTML = ''
    this.tokenize(value || '').forEach((part) => {
      if (part.token) this.editorTarget.appendChild(this.pill(part.token))
      else if (part.text) this.editorTarget.appendChild(document.createTextNode(part.text))
    })
  }

  tokenize (str) {
    const parts = []
    const re = /\{\{([^}]+)\}\}/g
    let last = 0
    let m
    while ((m = re.exec(str))) {
      if (m.index > last) parts.push({ text: str.slice(last, m.index) })
      parts.push({ token: m[1].trim() })
      last = re.lastIndex
    }
    if (last < str.length) parts.push({ text: str.slice(last) })
    return parts
  }

  pill (label) {
    const missing = this.missing(label)
    const span = document.createElement('span')
    span.contentEditable = 'false'
    span.dataset.label = label
    span.className = `${PILL} ${missing ? TONE_MISSING : TONE}`
    if (missing) span.title = MISSING_HINT
    span.textContent = label
    return span
  }

  missing (label) {
    return this.known.size > 0 && !this.known.has(label.trim().toLowerCase())
  }

  renderPreview () {
    if (!this.hasPreviewTarget) return
    const raw = this.serialize().replace(/\}\}\s*\{\{/g, '}} {{')
    if (!raw.trim()) {
      this.previewTarget.innerHTML = '<span class="text-gray-400 italic">the first short answer</span>'
      return
    }
    this.previewTarget.innerHTML = escapeHtml(raw).replace(/\{\{([^}]+)\}\}/g, (_, l) => this.previewPill(l.trim()))
  }

  previewPill (label) {
    const missing = this.missing(label)
    const hint = missing ? ` title="${MISSING_HINT}"` : ''
    return `<span${hint} class="${PREVIEW_PILL} ${missing ? TONE_MISSING : TONE}">${label}</span>`
  }

  insertNode (node) {
    const sel = window.getSelection()
    if (!sel.rangeCount || !this.editorTarget.contains(sel.anchorNode)) {
      this.editorTarget.appendChild(node)
      const space = document.createTextNode(' ')
      this.editorTarget.appendChild(space)
      this.placeCursorAfter(space)
      return
    }
    const range = sel.getRangeAt(0)
    range.deleteContents()
    range.insertNode(node)
    const space = document.createTextNode(' ')
    node.after(space)
    this.placeCursorAfter(space)
  }

  insertText (text) {
    const sel = window.getSelection()
    if (!sel.rangeCount || !this.editorTarget.contains(sel.anchorNode)) {
      this.editorTarget.appendChild(document.createTextNode(text))
      return
    }
    const range = sel.getRangeAt(0)
    range.deleteContents()
    const node = document.createTextNode(text)
    range.insertNode(node)
    this.placeCursorAfter(node)
  }

  placeCursorAfter (node) {
    const sel = window.getSelection()
    const range = document.createRange()
    range.setStartAfter(node)
    range.collapse(true)
    sel.removeAllRanges()
    sel.addRange(range)
  }
}
